// Widgets that keep their own content: notes, tasks, headings, sticky notes,
// links and habits. Everything is editable in place.

import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Check, Plus, Trash2, X, ExternalLink, Flame } from "lucide-react";
import { useSize } from "../charts/charts";
import { quarter } from "./layout";
import { HB, dayKey, habitDays, habitFit, habitNameWidth, habitStreak, linksFit, readHabits, readingType, stickyType } from "./plans";
import { useTick } from "./tick";
import { readTasks, type Task } from "../../shared/catalog";
import { newId } from "../../shared/ids";
import type { WidgetItem } from "../../shared/types";
import { cx, domainOf, favicon } from "../lib/util";
import { Linked } from "../lib/Linked";
import { updateItem, updateProps } from "../state/actions";
import { get, set, useStore } from "../state/store";
import { Markdown } from "./markdown";
import type { WP } from "./data";
import { colorOf, useAgentColors } from "../state/agentColors";
import { REWRITE, agentName, changedChars } from "../../shared/authors";

function useDebouncedSave(item: WidgetItem, key: string, delay = 500) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef<string | null>(null);
  const flush = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    const cur = get().items[item.id] as WidgetItem | undefined;
    if (latest.current !== null && latest.current !== String(cur?.props[key] ?? "")) updateProps(item.id, { [key]: latest.current }, "Edit text");
    latest.current = null;
  };
  useEffect(() => () => flush(), []); // eslint-disable-line react-hooks/exhaustive-deps
  return {
    change(v: string) {
      latest.current = v;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(flush, delay);
    },
    flush,
  };
}

export function Notes({ item, readOnly }: WP) {
  const text = String(item.props.text ?? "");
  const colors = useAgentColors();
  // the words as this time of writing began, to tell a typo from a rewrite of an agent's note (agent colours, 20 Oct)
  const began = useRef(text);
  const editing = useStore((s) => s.editing === item.id);
  const save = useDebouncedSave(item, "text");
  const [draft, setDraft] = useState(text);
  const [ref, size] = useSize<HTMLElement>();
  const px = readingType(size.w, 13.5, 17);
  useEffect(() => {
    if (!editing) setDraft(text);
  }, [text, editing]);
  if (editing && !readOnly)
    return (
      <textarea
        ref={ref}
        style={{ fontSize: px * 0.93 }}
        className="notes-edit"
        autoFocus
        value={draft}
        placeholder="Write anything. Markdown works: **bold**, - lists, [ ] tasks."
        onChange={(e) => {
          setDraft(e.target.value);
          save.change(e.target.value);
        }}
        onFocus={() => (began.current = text)}
        onBlur={() => {
          save.flush();
          set({ editing: null });
          if (item.by && changedChars(began.current, draft) > began.current.length * REWRITE) updateItem(item.id, { by: null }, "Edit note");
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") (e.target as HTMLTextAreaElement).blur();
          e.stopPropagation();
        }}
        data-interactive
      />
    );
  return (
    <div
      ref={ref}
      className={cx("notes", !text.trim() && "is-empty")}
      style={{ fontSize: px }}
      data-agent-color={colorOf(item.by, colors) ?? undefined}
      title={colors.on && item.by ? `Written by ${agentName(item.by)}` : undefined}
      data-scroll
      onDoubleClick={(e) => {
        if (readOnly) return;
        e.stopPropagation();
        set({ editing: item.id, selection: [item.id] });
      }}
      onClick={phoneTap(readOnly, () => set({ editing: item.id, selection: [item.id] }))}
    >
      {text.trim() ? <Markdown text={text} className="md em" /> : <span className="notes-placeholder">{readOnly ? "" : writeHint()}</span>}
    </div>
  );
}

/**
 * On a phone a tap starts writing, where a computer takes a double-click (a phone has no double-click to speak of).
 * A tap on a link in the words follows the link.
 */
function phoneTap(readOnly: boolean, edit: () => void) {
  return (e: React.MouseEvent) => {
    if (readOnly || !get().mobile || (e.target as Element).closest("a")) return;
    e.stopPropagation();
    edit();
  };
}

const writeHint = () => (get().mobile ? "Tap to write" : "Double-click to write");

export function Tasks({ item, readOnly }: WP) {
  const colors = useAgentColors();
  // the list as the board reads it, whatever an older board or an agent saved: every entry has words and an id
  // of its own, so a tick, an edit or a delete is for that one entry, and a save keeps the ids it shows
  const tasks = readTasks(item.props.tasks);
  const [text, setText] = useState("");
  const [editId, setEditId] = useState<string | null>(null);
  const save = (next: Task[], label = "Tasks") => updateProps(item.id, { tasks: next }, label);
  const open = tasks.filter((t) => !t.done).length;
  const add = () => {
    const v = text.trim();
    if (!v) return;
    save([...tasks, { id: newId("t"), text: v, done: false }], "Add task");
    setText("");
  };
  const [ref, size] = useSize<HTMLDivElement>();
  return (
    <div className="tasks" ref={ref} style={{ fontSize: readingType(size.w, 13, 16.5) }}>
      <div className="tasks-list" data-scroll>
        {tasks.map((t) => (
          <div key={t.id} className={cx("task", t.done && "is-done")} data-task-id={t.id}>
            <button className="check" aria-label={t.done ? "Mark not done" : "Mark done"} disabled={readOnly} onClick={() => save(tasks.map((x) => (x.id === t.id ? { ...x, done: !x.done } : x)), t.done ? "Reopen task" : "Tick task")} data-interactive>
              {t.done && <Check strokeWidth={3} />}
            </button>
            {editId === t.id ? (
              <input
                className="task-input"
                autoFocus
                defaultValue={t.text}
                onBlur={(e) => {
                  const v = e.currentTarget.value.trim();
                  setEditId(null);
                  // an agent's to-do rewritten, more than half of it, is the user's (agent colours, 20 Oct)
                  if (v && v !== t.text) save(tasks.map((x) => (x.id === t.id ? (x.by && changedChars(x.text, v) > x.text.length * REWRITE ? { ...x, text: v, by: undefined } : { ...x, text: v }) : x)), "Edit task");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === "Escape") e.currentTarget.blur();
                  e.stopPropagation();
                }}
                data-interactive
              />
            ) : (
              <span
                className="task-text"
                data-agent-color={colorOf(t.by, colors) ?? undefined}
                title={colors.on && t.by ? `Added by ${t.by}` : undefined}
                onDoubleClick={(e) => {
                  if (readOnly) return;
                  // the task opens to edit, and the widget's settings stay shut
                  e.stopPropagation();
                  setEditId(t.id);
                }}
                onClick={phoneTap(readOnly, () => setEditId(t.id))}
              >
                <Linked text={t.text} />
                {t.by && <span className="task-by">{t.by}</span>}
              </span>
            )}
            {!readOnly && (
              <button className="task-del" aria-label="Delete task" onClick={() => save(tasks.filter((x) => x.id !== t.id), "Delete task")} data-interactive>
                <X />
              </button>
            )}
          </div>
        ))}
        {!tasks.length && <div className="tasks-empty">{readOnly ? "Nothing to do" : "Nothing yet. Add one below, or ask your agent."}</div>}
      </div>
      {!readOnly && (
        <form
          className="task-add"
          onSubmit={(e) => {
            e.preventDefault();
            add();
          }}
          data-interactive
        >
          <Plus />
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder={open ? `${open} open. Add a task` : "Add a task"} onKeyDown={(e) => e.stopPropagation()} />
        </form>
      )}
    </div>
  );
}

export function Heading({ item, readOnly }: WP) {
  const editing = useStore((s) => s.editing === item.id);
  const text = String(item.props.text ?? "");
  const align = (item.props.align as string) ?? "left";
  if (editing && !readOnly)
    return (
      <input
        className={cx("heading", "heading-edit", `align-${align}`)}
        autoFocus
        defaultValue={text}
        onBlur={(e) => {
          set({ editing: null });
          if (e.currentTarget.value !== text) updateProps(item.id, { text: e.currentTarget.value }, "Edit heading");
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === "Escape") e.currentTarget.blur();
          e.stopPropagation();
        }}
        data-interactive
      />
    );
  return (
    <div
      className={cx("heading", `align-${align}`)}
      onDoubleClick={(e) => {
        if (readOnly) return;
        e.stopPropagation();
        set({ editing: item.id, selection: [item.id] });
      }}
      onClick={phoneTap(readOnly, () => set({ editing: item.id, selection: [item.id] }))}
    >
      {text ? <Linked text={text} /> : readOnly ? "" : "Heading"}
    </div>
  );
}

export const STICKY_COLORS: Record<string, [string, string]> = {
  butter: ["#fbe8a6", "#4a3f12"],
  peach: ["#fcd2bd", "#4d2615"],
  mint: ["#c9ecd2", "#18402a"],
  sky: ["#c8e1fb", "#183452"],
  lilac: ["#dcd0f7", "#2e2152"],
  rose: ["#f7cadb", "#4f1c32"],
  paper: ["#f4efe2", "#2e2a20"],
  ink: ["#20232b", "#eef0f5"],
};

export function Sticky({ item, readOnly }: WP) {
  const editing = useStore((s) => s.editing === item.id);
  const text = String(item.props.text ?? "");
  // a color the board knows, by its own name only: "constructor" is no color
  const named = String(item.props.color ?? "butter");
  const [bg, fg] = Object.hasOwn(STICKY_COLORS, named) ? STICKY_COLORS[named] : STICKY_COLORS.butter;
  const save = useDebouncedSave(item, "text");
  const [draft, setDraft] = useState(text);
  useEffect(() => {
    if (!editing) setDraft(text);
  }, [text, editing]);
  const tilt = ((item.id.charCodeAt(item.id.length - 1) % 5) - 2) * 0.45;
  // the words fill the note: big for a few, smaller as they grow. A first guess from the box inside its padding,
  // then the biggest size at which the words really fit, measured on the words themselves
  const [ref, size] = useSize<HTMLDivElement>();
  const words = editing ? draft : text;
  const guess = stickyType(size, words);
  const [px, setPx] = useState<number | null>(null);
  const [fonts, setFonts] = useState(0);
  useEffect(() => {
    let live = true;
    void document.fonts?.ready.then(() => live && setFonts((n) => n + 1));
    return () => {
      live = false;
    };
  }, []);
  useLayoutEffect(() => {
    const el = ref.current?.querySelector<HTMLElement>(".sticky-text");
    if (!el || !size.w || !size.h) return;
    const fits = () => el.scrollHeight <= el.clientHeight + 1 && el.scrollWidth <= el.clientWidth + 1;
    let lo = 12;
    let hi = Math.min(96, Math.max(guess * 1.6, 24));
    for (let i = 0; i < 9; i++) {
      const f = (lo + hi) / 2;
      el.style.fontSize = `${f}px`;
      if (fits()) lo = f;
      else hi = f;
    }
    el.style.fontSize = "";
    setPx(Math.max(12, Math.floor(lo)));
  }, [words, size.w, size.h, fonts, editing, guess, ref]);
  return (
    <div
      className="sticky"
      style={{ ["--paper" as string]: bg, color: fg, transform: `rotate(${tilt}deg)` }}
      // a double click anywhere on the paper writes in it, its margins too: a note that is only a link has no other words
      // to double-click (Round 7), since a press on a link is the link's
      onDoubleClick={(e) => {
        if (readOnly || editing) return;
        e.stopPropagation();
        set({ editing: item.id, selection: [item.id] });
      }}
    >
      <div className="sticky-box" ref={ref} style={{ fontSize: px ?? guess }}>
      {editing && !readOnly ? (
        <textarea
          className="sticky-text"
          autoFocus
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            save.change(e.target.value);
          }}
          onBlur={() => {
            save.flush();
            set({ editing: null });
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") (e.target as HTMLTextAreaElement).blur();
            e.stopPropagation();
          }}
          style={{ color: fg }}
          data-interactive
        />
      ) : (
        <div className="sticky-text" onClick={phoneTap(readOnly, () => set({ editing: item.id, selection: [item.id] }))}>
          {text ? <Linked text={text} /> : readOnly ? "" : <span className="sticky-hint">{writeHint()}</span>}
        </div>
      )}
      </div>
    </div>
  );
}

interface Link {
  id: string;
  url: string;
  label?: string;
}

export function Links({ item, readOnly }: WP) {
  const links = (item.props.links as Link[] | undefined) ?? [];
  const [adding, setAdding] = useState(false);
  const [url, setUrl] = useState("");
  const add = () => {
    let u = url.trim();
    if (!u) return;
    if (!/^https?:\/\//i.test(u)) u = "https://" + u;
    try {
      new URL(u);
    } catch {
      return;
    }
    updateProps(item.id, { links: [...links, { id: newId("l"), url: u, label: domainOf(u).split(".")[0].replace(/^./, (c) => c.toUpperCase()) }] }, "Add link");
    setUrl("");
    setAdding(false);
  };
  // like app icons: a few links get big ones, many get smaller, and a tall, narrow tile lists them
  const [ref, size] = useSize<HTMLDivElement>();
  const fit = linksFit(size, links.length + (readOnly ? 0 : 1));
  return (
    <div className={cx("links", `is-${fit.id}`)} ref={ref} style={{ fontSize: quarter(fit.px), ["--cols" as string]: fit.cols }} data-scroll>
      {links.map((l) => (
        <a key={l.id} className="link" href={l.url} target="_blank" rel="noreferrer noopener" title={fit.id === "icons" ? `${l.label || domainOf(l.url)}: ${l.url}` : l.url} data-interactive>
          <img src={favicon(l.url)} alt="" loading="lazy" onError={(e) => ((e.currentTarget as HTMLImageElement).style.visibility = "hidden")} />
          <span className="link-name">
            {l.label || domainOf(l.url)}
            {fit.id === "rows" && <small>{domainOf(l.url)}</small>}
          </span>
          {!readOnly && (
            <button
              className="link-del"
              aria-label="Remove link"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                updateProps(item.id, { links: links.filter((x) => x.id !== l.id) }, "Remove link");
              }}
            >
              <X />
            </button>
          )}
        </a>
      ))}
      {!readOnly &&
        (adding ? (
          <form
            className="link-add-form"
            onSubmit={(e) => {
              e.preventDefault();
              add();
            }}
            data-interactive
          >
            <input autoFocus placeholder="stripe.com" value={url} onChange={(e) => setUrl(e.target.value)} onBlur={() => !url && setAdding(false)} onKeyDown={(e) => {
              if (e.key === "Escape") setAdding(false);
              e.stopPropagation();
            }} />
          </form>
        ) : (
          <button className="link link-add" onClick={() => setAdding(true)} title="Add a link" aria-label="Add a link" data-interactive>
            <Plus />
            <span className="link-name">{links.length ? "Add" : "Add a link"}</span>
          </button>
        ))}
      {!links.length && readOnly && <span className="faint">No links</span>}
    </div>
  );
}

interface Habit {
  id: string;
  name: string;
}

const LONG_DAY = (d: Date) => d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

export function Habits({ item, readOnly }: WP) {
  const habits: Habit[] = readHabits(item.props.habits);
  const log = (item.props.log as Record<string, string[]> | undefined) ?? {};
  const [adding, setAdding] = useState(false);
  const [ref, size] = useSize<HTMLDivElement>();
  useTick(60_000);
  const today = new Date();
  const todayKey = dayKey(today);
  const foot = !readOnly;
  const fit = habitFit(size, habits.length, foot, habitNameWidth(habits.map((h) => h.name)));
  const toggle = (hid: string, key: string) => {
    const cur = new Set(log[key] ?? []);
    if (cur.has(hid)) cur.delete(hid);
    else cur.add(hid);
    updateProps(item.id, { log: { ...log, [key]: [...cur] } }, cur.has(hid) ? "Tick habit" : "Untick habit");
  };
  const remove = (h: Habit) => updateProps(item.id, { habits: habits.filter((x) => x.id !== h.id) }, "Remove habit");
  const addForm = adding ? (
    <form
      className="hb-addform"
      onSubmit={(e) => {
        e.preventDefault();
        const v = (e.currentTarget.elements.namedItem("h") as HTMLInputElement).value.trim();
        if (v) updateProps(item.id, { habits: [...habits, { id: newId("h"), name: v }] }, "Add habit");
        setAdding(false);
      }}
      data-interactive
    >
      <Plus />
      <input name="h" autoFocus placeholder="A habit, like Wrote 500 words" onBlur={() => setAdding(false)} onKeyDown={(e) => e.stopPropagation()} />
    </form>
  ) : (
    <button className="hb-add" onClick={() => setAdding(true)} data-interactive>
      <Plus /> Habit
    </button>
  );
  const style = fit.px ? { fontSize: quarter(fit.px) } : undefined;
  if (!habits.length)
    return (
      <div className="hb hb-empty" ref={ref}>
        <span className="hb-empty-ico">
          <Flame />
        </span>
        <b>Track a habit</b>
        <span>Tick each day you do it, and watch the streak grow.</span>
        {!readOnly &&
          (adding ? (
            addForm
          ) : (
            <button className="btn btn-sm btn-primary hb-first" onClick={() => setAdding(true)} data-interactive>
              <Plus size={13} /> Add a habit
            </button>
          ))}
      </div>
    );
  if (fit.layout === "today")
    return (
      <div className="hb is-today" ref={ref} style={style}>
        {fit.head && <span className="hb-today-head">Today, {today.toLocaleDateString("en-US", { weekday: "long" })}</span>}
        <div className="hb-today">
          {habits.slice(0, fit.shown).map((h, i) => {
            const on = (log[todayKey] ?? []).includes(h.id);
            const n = habitStreak(log, h.id, today);
            return (
              <button key={h.id} className={cx("hb-tick", on && "is-on")} disabled={readOnly} onClick={() => toggle(h.id, todayKey)} aria-pressed={on} aria-label={`${h.name}, today`} data-interactive>
                <span className="hb-check">{on && <Check strokeWidth={3} />}</span>
                <span className="hb-name">{h.name}</span>
                {i === fit.shown - 1 && habits.length > fit.shown && (
                  <em className="hb-more" title={`${habits.length - fit.shown} more habit${habits.length - fit.shown === 1 ? "" : "s"} show in a bigger tile`}>
                    +{habits.length - fit.shown}
                  </em>
                )}
                <span className={cx("hb-streak", n > 0 && "is-on")}>
                  {n > 0 && <Flame />}
                  {n}
                </span>
              </button>
            );
          })}
        </div>
        {fit.foot && addForm}
      </div>
    );
  const days = habitDays(today, fit.weeks);
  // the grid's columns: the names (beside the days only), each week's days with a wider gap between weeks, and the streaks
  const stacked = fit.layout === "stack";
  const weeksCols = Array.from({ length: fit.weeks }, () => `repeat(7, ${HB.cell}em)`).join(` ${HB.week - HB.gap}em `);
  const cols = `${stacked ? "" : "minmax(0, 1fr) "}${weeksCols} ${HB.streak}em`;
  const first = stacked ? 1 : 2;
  const at = (i: number) => first + i + Math.floor(i / 7);
  const streakCol = at(days.length - 1) + 1;
  // a habit's rows: its name and days on one, or its name on one and its days under it
  const rowOf = (r: number) => (stacked ? 3 + 2 * r : 2 + r);
  return (
    <div className={cx("hb", stacked ? "is-stack" : "is-grid")} ref={ref} style={style}>
      <div className="hb-grid" style={{ gridTemplateColumns: cols }} aria-label={`${item.title || "Habits"}, ${fit.weeks === 1 ? "this week" : `the last ${fit.weeks} weeks`}`}>
        {days.map((d, i) => {
          const key = dayKey(d);
          return (
            <span key={key} className={cx("hb-day", key === todayKey && "is-today", d > today && key !== todayKey && "is-ahead")} style={{ gridColumn: at(i), gridRow: 1 }} title={LONG_DAY(d)}>
              <b>{"MTWTFSS"[i % 7]}</b>
              <small>{d.getDate()}</small>
            </span>
          );
        })}
        <span className="hb-streak-head" style={{ gridColumn: streakCol, gridRow: 1 }} title="Days in a row">
          <Flame />
        </span>
        {habits.map((h, r) => {
          const n = habitStreak(log, h.id, today);
          return (
            <div key={h.id} className="hb-row">
              <span className="hb-name" style={stacked ? { gridColumn: `1 / ${streakCol + 1}`, gridRow: rowOf(r) - 1 } : { gridColumn: 1, gridRow: rowOf(r) }} title={h.name}>
                <span>{h.name}</span>
                {!readOnly && (
                  <button className="hb-del" onClick={() => remove(h)} aria-label={`Remove ${h.name}`} title="Remove" data-interactive>
                    <Trash2 />
                  </button>
                )}
              </span>
              {days.map((d, i) => {
                const key = dayKey(d);
                const on = (log[key] ?? []).includes(h.id);
                const ahead = d > today && key !== todayKey;
                return (
                  <button
                    key={key}
                    className={cx("hb-cell", on && "is-on", key === todayKey && "is-today", ahead && "is-ahead")}
                    style={{ gridColumn: at(i), gridRow: rowOf(r) }}
                    disabled={readOnly || ahead}
                    onClick={() => toggle(h.id, key)}
                    aria-pressed={on}
                    aria-label={`${h.name}, ${LONG_DAY(d)}`}
                    title={`${h.name}, ${LONG_DAY(d)}${on ? ": done" : ""}`}
                    data-interactive
                  />
                );
              })}
              <span className={cx("hb-streak", n > 0 && "is-on")} style={{ gridColumn: streakCol, gridRow: rowOf(r) }} title={`${n} day${n === 1 ? "" : "s"} in a row`}>
                {n}
              </span>
            </div>
          );
        })}
      </div>
      {foot && addForm}
    </div>
  );
}

export { ExternalLink };
